import React, { useState } from "react";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { Mail, MapPin, Phone, ArrowUpRight, Loader2 } from "lucide-react";
import Reveal from "../motion/Reveal";
import { site } from "../../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INITIAL = { name: "", company: "", phone: "", email: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(INITIAL);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [consent, setConsent] = useState(false);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
    setErrors((er) => ({ ...er, [name]: undefined }));
  };

  const validate = () => {
    const err = {};
    if (!form.name.trim() || form.name.trim().length < 2) err.name = "Bitte Namen angeben.";
    if (!form.email.trim() || !/.+@.+\..+/.test(form.email)) err.email = "Bitte gültige E-Mail-Adresse angeben.";
    if (!form.message.trim() || form.message.trim().length < 5) err.message = "Bitte eine kurze Nachricht schreiben.";
    if (!consent) err.consent = "Bitte der Datenschutzerklärung zustimmen.";
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate() || loading) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/contact`, form);
      toast.success(data?.message || "Vielen Dank! Wir melden uns in Kürze.");
      setForm(INITIAL);
      setConsent(false);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Nachricht konnte nicht gesendet werden.";
      toast.error(typeof msg === "string" ? msg : "Nachricht konnte nicht gesendet werden.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="kontakt"
      className="relative bg-white py-32 lg:py-44 border-t border-brand-border"
      data-testid="contact-section"
    >
      <Toaster position="top-right" richColors closeButton />
      <div className="max-w-[90rem] mx-auto px-6 sm:px-8 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          {/* Left: info */}
          <div className="lg:col-span-5">
            <Reveal>
              <p className="eyebrow">Kontakt · Persönlich</p>
            </Reveal>
            <Reveal delay={0.05}>
              <h2 className="mt-4 font-display font-medium text-4xl sm:text-5xl lg:text-[3.75rem] leading-[1.02] tracking-tighter text-balance">
                Reden wir. Persönlich.
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-6 text-brand-muted text-lg leading-relaxed max-w-md">
                Schreiben Sie uns über das Formular oder direkt per E-Mail.
                Sie erhalten eine persönliche Antwort — von Björn, nicht von
                einem Bot.
              </p>
            </Reveal>

            <Reveal delay={0.12}>
              <div className="mt-6 inline-flex items-center gap-2.5 px-4 py-2 rounded-full bg-brand-success/10 border border-brand-success/25 text-brand-primary text-sm">
                <span className="relative flex h-2.5 w-2.5">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-brand-success opacity-70 animate-ping" />
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-brand-success" />
                </span>
                <span className="font-medium">
                  Antwort meist innerhalb weniger Stunden
                </span>
              </div>
            </Reveal>

            <Reveal delay={0.15}>
              <ul className="mt-10 space-y-5 text-brand-primary">
                <li className="flex items-start gap-4">
                  <span className="mt-0.5 h-10 w-10 rounded-2xl bg-brand-light border border-brand-border grid place-items-center">
                    <MapPin className="h-4 w-4 text-brand-accent" />
                  </span>
                  <address className="not-italic leading-relaxed">
                    {site.address.street}
                    <br />
                    {site.address.zip} {site.address.city}, {site.address.country}
                  </address>
                </li>
                <li className="flex items-center gap-4">
                  <span className="h-10 w-10 rounded-2xl bg-brand-light border border-brand-border grid place-items-center">
                    <Mail className="h-4 w-4 text-brand-accent" />
                  </span>
                  <a
                    href={`mailto:${site.email}`}
                    className="hover:text-brand-accent transition-colors"
                  >
                    {site.email}
                  </a>
                </li>
                <li className="flex items-center gap-4">
                  <span className="h-10 w-10 rounded-2xl bg-brand-light border border-brand-border grid place-items-center">
                    <Phone className="h-4 w-4 text-brand-accent" />
                  </span>
                  <a
                    href={`tel:${site.phone.replace(/[\s()\-]/g, "")}`}
                    className="hover:text-brand-accent transition-colors"
                  >
                    {site.phone}
                  </a>
                </li>
              </ul>
            </Reveal>

            <Reveal delay={0.2}>
              <div className="mt-10 overflow-hidden rounded-3xl border border-brand-border">
                <iframe
                  title="Metalotsen Standort Leipzig – Google Maps"
                  src={site.maps.embed}
                  loading="lazy"
                  className="w-full h-64"
                  referrerPolicy="no-referrer-when-downgrade"
                  data-testid="contact-map"
                />
              </div>
            </Reveal>
          </div>

          {/* Right: form */}
          <div className="lg:col-span-7">
            <Reveal>
              <form
                onSubmit={onSubmit}
                className="relative p-8 lg:p-10 rounded-3xl bg-brand-primary text-white overflow-hidden"
                data-testid="contact-form"
                noValidate
              >
                <div className="absolute inset-0 bg-grid opacity-30 pointer-events-none" aria-hidden />
                <div
                  aria-hidden
                  className="absolute -top-24 -right-24 w-[400px] h-[400px] rounded-full glow-radial"
                />

                {/*
                 * Terminplaner-Slot: sobald in `site.scheduling.embedUrl` eine
                 * Calendly- / Microsoft-Bookings-URL hinterlegt wird, erscheint
                 * hier automatisch ein Button darüber – ohne Codeänderung.
                 */}
                {site.scheduling?.embedUrl && (
                  <a
                    href={site.scheduling.embedUrl}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="contact-scheduler"
                    className="relative mb-6 -mt-2 flex items-center justify-between gap-4 px-5 py-4 rounded-2xl border border-white/10 bg-white/5 hover:bg-white/10 transition-colors"
                  >
                    <span className="text-sm text-white/80">
                      {site.scheduling.label}
                    </span>
                    <span className="inline-flex items-center gap-1 text-sm font-medium text-brand-accent">
                      Termin wählen <ArrowUpRight className="h-4 w-4" />
                    </span>
                  </a>
                )}

                <div className="relative">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <Field
                      label="Name*"
                      name="name"
                      value={form.name}
                      onChange={onChange}
                      error={errors.name}
                      autoComplete="name"
                    />
                    <Field
                      label="Unternehmen"
                      name="company"
                      value={form.company}
                      onChange={onChange}
                      autoComplete="organization"
                    />
                    <Field
                      label="E-Mail*"
                      name="email"
                      type="email"
                      value={form.email}
                      onChange={onChange}
                      error={errors.email}
                      autoComplete="email"
                    />
                    <Field
                      label="Telefon"
                      name="phone"
                      type="tel"
                      value={form.phone}
                      onChange={onChange}
                      autoComplete="tel"
                    />
                  </div>

                  <div className="mt-5">
                    <label className="block text-xs uppercase tracking-widest text-white/60 mb-2">
                      Nachricht*
                    </label>
                    <textarea
                      name="message"
                      rows={5}
                      value={form.message}
                      onChange={onChange}
                      data-testid="contact-field-message"
                      className={`w-full px-5 py-4 rounded-2xl bg-white/5 border ${
                        errors.message ? "border-red-400/60" : "border-white/10"
                      } focus:border-brand-accent focus:ring-2 focus:ring-brand-accent/30 transition-all outline-none text-white placeholder:text-white/30 resize-none`}
                      placeholder="Erzählen Sie uns kurz, wobei wir helfen können…"
                    />
                    {errors.message && (
                      <p className="mt-2 text-red-300 text-sm">{errors.message}</p>
                    )}
                  </div>

                  <label className="mt-6 flex items-start gap-3 text-sm text-white/70">
                    <input
                      type="checkbox"
                      checked={consent}
                      onChange={(e) => {
                        setConsent(e.target.checked);
                        setErrors((er) => ({ ...er, consent: undefined }));
                      }}
                      data-testid="contact-consent"
                      className="mt-1 h-4 w-4 rounded border-white/30 bg-white/10 accent-brand-accent"
                    />
                    <span>
                      Ich habe die{" "}
                      <a
                        href="/datenschutz"
                        className="underline hover:text-white"
                      >
                        Datenschutzerklärung
                      </a>{" "}
                      gelesen und stimme der Verarbeitung meiner Anfrage zu.
                    </span>
                  </label>
                  {errors.consent && (
                    <p className="mt-2 text-red-300 text-sm">{errors.consent}</p>
                  )}

                  <div className="mt-8 flex items-center justify-between gap-6">
                    <p className="text-xs text-white/60">
                      Antwort meist innerhalb weniger Stunden.
                    </p>
                    <button
                      type="submit"
                      disabled={loading}
                      data-testid="contact-submit"
                      className="btn-primary disabled:opacity-70 disabled:cursor-not-allowed"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" /> Senden…
                        </>
                      ) : (
                        <>
                          Anfrage senden <ArrowUpRight className="h-4 w-4" />
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </form>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, name, value, onChange, error, type = "text", autoComplete }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-widest text-white/60 mb-2">
        {label}
      </label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        autoComplete={autoComplete}
        data-testid={`contact-field-${name}`}
        className={`w-full px-5 py-4 rounded-2xl bg-white/5 border ${
          error ? "border-red-400/60" : "border-white/10"
        } focus:border-brand-accent focus:ring-2 focus:ring-brand-accent/30 transition-all outline-none text-white placeholder:text-white/30`}
      />
      {error && <p className="mt-2 text-red-300 text-sm">{error}</p>}
    </div>
  );
}
